import { Checkbox, Select, Stack, Text } from "@mantine/core"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { icons } from "@/components"
import { useConfig, usePermissions } from "@/hooks"
import { checkSurvey, type IMetaTeamFeedback, type TSession, type TVisibility } from "@/types"
import { AudienceManualSettings, AudiencePeersAutoSettings, AudienceSelector } from "./AudienceSelector"
import type { IStepInitialValues, IStepProps } from "./types"
import { PreviewPeers, PreviewRow, PreviewUsers } from "./utils"

export function AudienceStep({ activity, form }: IStepProps) {
  const { t } = useTranslation()

  const isSurvey = checkSurvey(activity)
  const isFeedback = isSurvey && activity.type === "feedback"
  const isOnboarding = isSurvey && activity.type === "onboarding"

  const { isAdmin, isManager } = usePermissions()
  const config = useConfig()
  const population = form.getInputProps("population").value || "all"
  const meta = (form.values.meta || {}) as IMetaTeamFeedback
  const visibilityData = visibilityOptions({ isAdmin, t })
  const sectionVisibility = config?.sections?.[activity.type]?.meta?.visibility as TVisibility | undefined

  useEffect(() => {
    // Regular users can only run public sessions
    if (!isManager) {
      form.setFieldValue("visibility", "public")
    } else if (!form.values.visibility) {
      form.setFieldValue("visibility", sectionVisibility || "public")
    }
  }, [sectionVisibility, form.values.visibility, isManager])

  const { peers = "all" } = meta
  const peersMode = peers === "all" ? "all" : ["teammates", "manager", "self"].includes(peers) ? "auto" : "manual"

  return (
    <Stack>
      {/* Main audience selector */}
      <AudienceSelector
        mode={population}
        error={
          form.values.population === "manual" && !meta.team_ids?.length && !meta.user_ids?.length
            ? t("Please choose at least one")
            : null
        }
        onChangeMode={(mode) => form.setFieldValue("population", mode as TSession["population"])}
        segments={[
          { value: "all", label: t("All employees") },
          { value: "manual", label: t("Choose") },
        ]}
        label={
          checkSurvey({ type: activity.type }) ? t("Select respondents") : t("Choose the audience for this session")
        }
      >
        <AudienceManualSettings
          teamProps={form.getInputProps("meta.team_ids")}
          userProps={form.getInputProps("meta.user_ids")}
        />
      </AudienceSelector>

      {population !== "manual" && (
        <Checkbox
          label={t("Only those who haven't participated yet")}
          {...form.getInputProps("meta.onboarding", { type: "checkbox" })}
        />
      )}

      {isSurvey && !isOnboarding && (
        <Checkbox label={t("Allow anonymity")} {...form.getInputProps("is_anonymous", { type: "checkbox" })} />
      )}

      {isFeedback && (
        <Stack gap={12} mt={8}>
          <AudienceSelector
            label={t("Choose a method to select peers for the review")}
            mode={peersMode}
            error={
              meta.peers === "manual" && !meta.peer_ids?.length && !meta.peer_team_ids?.length
                ? t("Please choose at least one")
                : null
            }
            segments={[
              { value: "all", label: t("All employees") },
              { value: "auto", label: t("Automatically") },
              { value: "manual", label: t("Choose") },
            ]}
            onChangeMode={(mode) => {
              if (mode === "all") {
                form.setFieldValue("meta.peers", "all")
              } else if (mode === "auto") {
                form.setFieldValue("meta.peers", "teammates")
              } else {
                form.setFieldValue("meta.peers", "manual")
              }
            }}
          >
            <AudienceManualSettings
              teamProps={form.getInputProps("meta.peer_team_ids")}
              userProps={form.getInputProps("meta.peer_ids")}
            />
            <AudiencePeersAutoSettings {...form.getInputProps("meta.peers")} />
          </AudienceSelector>
        </Stack>
      )}
      {isManager && (
        <Select
          w={400}
          allowDeselect={false}
          label={t("Who can see results")}
          data={visibilityData}
          {...form.getInputProps("visibility")}
        />
      )}

      {isFeedback && (
        <Checkbox
          label={t("Show anonymized results to reviewees")}
          styles={{ label: { maxWidth: 300 } }}
          {...form.getInputProps("has_peer_report", { type: "checkbox" })}
        />
      )}
    </Stack>
  )
}

AudienceStep.initialValues = ({ session, activity }: IStepInitialValues) => {
  const meta = (session?.meta || {
    onboarding: activity?.type === "onboarding",
  }) as IMetaTeamFeedback
  if (activity?.type === "feedback") meta.peers = meta.peers || "all"

  return {
    meta,
    population: ((session?.population as string) === "teams" ? "manual" : session?.population) || "all",
    is_anonymous: session?.is_anonymous || false,
    has_peer_report: session?.has_peer_report ?? true,
  }
}

AudienceStep.validate = ({ meta, population }: ReturnType<typeof AudienceStep.initialValues>, t) => {
  const res = {
    population: !population ? t("This field is required") : null,
  }
  if (population === "manual") {
    const hasPeople = meta.team_ids?.length || meta.user_ids?.length
    res.population = !hasPeople ? t("Please choose at least one") : null
  }

  if (meta.peers) {
    const hasPeers = meta.peer_ids?.length || meta.peer_team_ids?.length
    res["meta.peers"] = meta.peers === "manual" && !hasPeers ? t("Please choose at least one") : null
  }
  return res
}

AudienceStep.transformValues = ({ meta, population, has_peer_report, ...values }: TSession) => {
  const resolvedMeta = { ...meta } as IMetaTeamFeedback & { [key: string]: unknown }
  // Clean audience selection when "all"
  if (population === "all") {
    resolvedMeta.team_ids = []
    resolvedMeta.user_ids = []
  }
  // Clean peers selection when "all"
  if (resolvedMeta.peers === "all") {
    resolvedMeta.peer_ids = []
    resolvedMeta.peer_team_ids = []
  }

  const hasTeams = resolvedMeta.team_ids?.length
  const hasUsers = resolvedMeta.user_ids?.length
  let resolvedPopulation = population
  if (hasUsers || hasTeams) {
    resolvedPopulation = "manual"
  } else {
    resolvedPopulation = "all"
  }

  return {
    ...values,
    has_peer_report,
    population: resolvedPopulation,
    meta: {
      ...resolvedMeta,
      onboarding: resolvedPopulation === "manual" ? false : resolvedMeta.onboarding,
    },
  }
}

AudienceStep.label = "Audience"

AudienceStep.Preview = ({ activity, form }) => {
  const { t } = useTranslation()
  const { meta, population, is_anonymous, visibility, has_peer_report } = AudienceStep.transformValues(
    form.values as any,
  )
  return (
    <>
      <PreviewRow
        value={
          <>
            <Text inherit>
              <PreviewUsers method={population} user_ids={meta.user_ids} team_ids={meta.team_ids} />
            </Text>
            {meta.onboarding && <Text c="dimmed">{`(${t("only onboarding")})`}</Text>}
          </>
        }
      >
        <icons.Team />
        {t("Participants")}
      </PreviewRow>
      {activity.type === "feedback" && <PreviewPeers {...(meta as any)} />}
      {checkSurvey(activity) && activity.type != "onboarding" && (
        <>
          <PreviewRow value={is_anonymous ? t("Allowed") : t("No")}>
            <icons.Anonymous />
            {t("Anonymity")}
          </PreviewRow>

          <PreviewRow value={t(visibility)}>
            <icons.Visibility />
            {t("Visibility")}
          </PreviewRow>

          {has_peer_report && (
            <PreviewRow value={t("Yes")}>
              <icons.Visibility />
              {t("Show anonymized results to reviewees")}
            </PreviewRow>
          )}
        </>
      )}
    </>
  )
}

function visibilityOptions({ isAdmin, t }: { isAdmin?: boolean; t: (key: string) => string }) {
  const data = [
    { value: "public", label: t("All employees") },
    { value: "managers", label: t("managers") },
  ]
  if (isAdmin) data.push({ value: "admins", label: t("admins") })
  return data
}
